import { Component, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { RoleService } from 'src/app/services/role.service';
import { AppService } from 'src/app/services/app.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  // User role permissions
  isManager = false;
  isSuperAdmin = false;

  // Search functionality
  searchTerm = '';

  // Dropdown menu states
  assessmentExpanded = false;
  printExpanded = false;
  taxExpanded = false;

  // Resize functionality
  @ViewChild('sidebar', { static: true }) sidebarRef!: ElementRef;
  isResizing = false;
  startX = 0;
  startWidth = 0;

  constructor(
    private authService: AuthService,
    public roleService: RoleService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.checkUserPermissions();
    // Initialize CSS custom property for sidebar width
    this.updateSidebarWidth();
    // Initialize scroll indicators and add scroll listener
    setTimeout(() => {
      this.updateScrollIndicators();
      this.addScrollListener();
    }, 100);
  }

  private updateSidebarWidth(): void {
    const width = this.isPinned() ? 80 : this.getSidebarWidth();
    document.documentElement.style.setProperty('--sidebar-width', `${width}px`);
  }

  // Check user permissions based on roles
  checkUserPermissions(): void {
    this.isManager = this.roleService.hasRole('ROLE_MANAGER');
    this.isSuperAdmin = this.roleService.isSuperAdmin();
  }

  // Search functionality
  filterMenu(): void {
    // Implementation for filtering menu items based on search term
    // This can be enhanced to actually filter the menu items
    console.log('Filtering menu with term:', this.searchTerm);
  }

  // Dropdown toggle methods
  toggleAssessmentMenu(): void {
    this.assessmentExpanded = !this.assessmentExpanded;
    // Close other dropdowns
    this.printExpanded = false;
    this.taxExpanded = false;
    // Update scroll indicators after animation
    setTimeout(() => this.updateScrollIndicators(), 300);
  }

  togglePrintMenu(): void {
    this.printExpanded = !this.printExpanded;
    // Close other dropdowns
    this.assessmentExpanded = false;
    this.taxExpanded = false;
    // Update scroll indicators after animation
    setTimeout(() => this.updateScrollIndicators(), 300);
  }

  toggleTaxMenu(): void {
    this.taxExpanded = !this.taxExpanded;
    // Close other dropdowns
    this.assessmentExpanded = false;
    this.printExpanded = false;
    // Update scroll indicators after animation
    setTimeout(() => this.updateScrollIndicators(), 300);
  }

  // Close all dropdowns
  closeAllDropdowns(): void {
    this.assessmentExpanded = false;
    this.printExpanded = false;
    this.taxExpanded = false;
  }

  // Resize functionality
  onResizeStart(event: MouseEvent): void {
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.sidebarRef.nativeElement.offsetWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    this.sidebarRef.nativeElement.classList.add('resizing');
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isResizing) {
      const deltaX = event.clientX - this.startX;
      const newWidth = this.startWidth + deltaX;
      this.setSidebarWidth(newWidth);
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.sidebarRef.nativeElement.classList.remove('resizing');
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    // Update scroll indicators when window resizes
    setTimeout(() => this.updateScrollIndicators(), 100);
  }

  setSidebarWidth(width: number): void {
    this.appService.setSidebarWidth(width);
    this.sidebarRef.nativeElement.style.width = `${this.appService.getSidebarStat().sidebarWidth}px`;
    // Update CSS custom property for content area margin
    this.updateSidebarWidth();
  }

  getSidebarWidth(): number {
    return this.appService.getSidebarStat().sidebarWidth;
  }

  togglePin(): void {
    this.appService.toggleSidebarPin();
    // Update CSS custom property based on pinned state
    this.updateSidebarWidth();
  }

  isPinned(): boolean {
    return this.appService.getSidebarStat().isSidebarPinned;
  }

  isToggled(): boolean {
    return this.appService.getSidebarStat().isSidebarToggled;
  }

  // Scroll indicator methods
  updateScrollIndicators(): void {
    const container = this.sidebarRef.nativeElement.querySelector('.scrollable-content');
    if (!container) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    // Add/remove scroll indicator classes on the scrollable container
    if (scrollTop > 20) {
      container.classList.add('has-scroll-top');
    } else {
      container.classList.remove('has-scroll-top');
    }

    if (scrollBottom > 20) {
      container.classList.add('has-scroll-bottom');
    } else {
      container.classList.remove('has-scroll-bottom');
    }
  }

  addScrollListener(): void {
    const container = this.sidebarRef.nativeElement.querySelector('.scrollable-content');
    if (container) {
      container.addEventListener('scroll', () => {
        this.updateScrollIndicators();
      }, { passive: true });
    }
  }
}
